import { Camera } from '@babylonjs/core/Cameras/camera';
import { Matrix, Vector3 } from '@babylonjs/core/Maths';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { WebXRCamera } from '@babylonjs/core/XR/webXRCamera';

export interface IScreenPosition {
    x: number;
    y: number;
}

export interface IScreenBoundingBox extends IScreenPosition {
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * Generic utilities for the babylon scene
 */

/** returns the x,y pixel position of the node on the canvas */
export function nodeToScreen(bNode: TransformNode, camera: Camera) {
    camera = getCamera(camera);
    const nodeWorldPos = bNode.getAbsolutePosition(); // Vector3.TransformCoordinates(bNode.position, bNode.getWorldMatrix());
    return toScreen(nodeWorldPos, camera);
}

/** converts screen units */
export function toPixels(viewportUnits: IScreenPosition, camera: Camera) {
    camera = getCamera(camera);
    const canvasRect = camera.getEngine().getRenderingCanvasClientRect() as ClientRect;
    return {
        x: viewportUnits.x * canvasRect.width,
        y: viewportUnits.y * canvasRect.height,
    } as IScreenPosition;
}

/** returns the screen position as normalized device units a ratio from 0 to 1 */
export function toNDC(worldPos: Vector3, camera: Camera) {
    camera = getCamera(camera);
    const pos = Vector3.Project(worldPos, Matrix.IdentityReadOnly, camera.getTransformationMatrix(), camera.viewport);
    return {
        x: pos.x,
        y: pos.y,
    } as IScreenPosition;
}

export function toScreen(worldPos: Vector3, camera: Camera) {
    camera = getCamera(camera);
    const scene = camera.getScene();
    const engine = scene.getEngine();
    const pos = Vector3.Project(
        worldPos,
        Matrix.IdentityReadOnly,
        scene.getTransformMatrix(),
        camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight())
    );
    return {
        x: pos.x,
        y: pos.y,
    } as IScreenPosition;
}

export function getScreenBoundingBox(
    node: TransformNode,
    camera: Camera,
    includeDescendants = true
): IScreenBoundingBox {
    camera = getCamera(camera);
    const bv = getHierarchyBoundingVectors(node, includeDescendants);
    return boundingVectorsToScreen(bv, camera);
}

// export function getBoundingVectors(bNode: AbstractMesh | TransformNode | undefined, scene: Scene, includeDescendants = true) {
//     let bv: { min: Vector3, max: Vector3 } | undefined;
//     if (!bNode) {
//         bv = scene.getWorldExtends();
//     } else {
//         bv = bNode.getHierarchyBoundingVectors(true);
//         if (bv.min.x == Number.MAX_VALUE && bv.min.y == Number.MAX_VALUE && bv.min.z == Number.MAX_VALUE && bv.max.x == -Number.MAX_VALUE && bv.max.y == -Number.MAX_VALUE && bv.max.z == -Number.MAX_VALUE) {
//             bv = {
//                 min: bNode.absolutePosition.clone(),
//                 max: bNode.absolutePosition.clone()
//             }
//         }
//     }

//     return bv;
// }

/**
 * Babylon's getHierarchyBoundingVectors has a bug that surfaces when called on a transformNode that has no real meshes in it's child hierarchy.
 * Use this wrapper instead
 * @param bNode
 * @param includeDescendants
 */
export function getHierarchyBoundingVectors(bNode: AbstractMesh | TransformNode, includeDescendants = true) {
    let bv = bNode.getHierarchyBoundingVectors(includeDescendants);
    //to handle a babylon bug that returns maxvalues when the node is an empty transformnode
    if (
        bv.min.x == Number.MAX_VALUE &&
        bv.min.y == Number.MAX_VALUE &&
        bv.min.z == Number.MAX_VALUE &&
        bv.max.x == -Number.MAX_VALUE &&
        bv.max.y == -Number.MAX_VALUE &&
        bv.max.z == -Number.MAX_VALUE
    ) {
        bv = {
            min: bNode.absolutePosition.clone(),
            max: bNode.absolutePosition.clone(),
        };
    }

    return bv;
}

export function boundingVectorsToScreen(
    boundingVectors: { min: Vector3; max: Vector3 },
    camera: Camera
): IScreenBoundingBox {
    camera = getCamera(camera);
    const bv = boundingVectors;
    const xs = new Array<number>();
    const ys = new Array<number>();

    for (let i = 0; i < 8; i++) {
        const x = bv[Math.floor(i / 4) ? 'max' : 'min'].x;
        const y = bv[Math.floor(i / 2) % 2 ? 'max' : 'min'].y;
        const z = bv[i % 2 ? 'max' : 'min'].z;
        const v = new Vector3(x, y, z);
        const sp = toNDC(v, camera);
        xs.push(sp.x);
        ys.push(sp.y);
    }

    const minx = Math.min(...xs);
    const maxx = Math.max(...xs);
    const miny = Math.min(...ys);
    const maxy = Math.max(...ys);

    const width = maxx - minx;
    const height = maxy - miny;
    const x = minx + width / 2;
    const y = miny + height / 2;
    const engine = camera.getEngine();
    const canvas = engine.getRenderingCanvas()!;
    const canvasWidth = canvas.width * engine.getHardwareScalingLevel();
    const canvasHeight = canvas.height * engine.getHardwareScalingLevel();

    return {
        x: x * canvasWidth,
        y: y * canvasHeight,
        top: miny * canvasHeight,
        left: minx * canvasWidth,
        width: width * canvasWidth,
        height: height * canvasHeight,
    } as IScreenBoundingBox;
}

export function updateMatrixOfAncestorChain(ancestor: TransformNode, child: TransformNode) {
    while (child != ancestor && child instanceof TransformNode) {
        child.computeWorldMatrix(true);
        child = child.parent as TransformNode;
    }
    if (child) {
        child.computeWorldMatrix(true);
    }
}

function getCamera(camera: Camera) {
    if (camera instanceof WebXRCamera) {
        return camera.rigCameras[0];
    }
    return camera;
}
